import React, { useEffect, useRef } from 'react';
interface AnimatedBackgroundProps {
  type: 'cosmic' | 'garden' | 'transition';
  intensity?: 'low' | 'medium' | 'high';
}
const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  type = 'cosmic',
  intensity = 'medium'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Set canvas dimensions
    const setCanvasDimensions = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasDimensions();
    window.addEventListener('resize', setCanvasDimensions);
    // Determine particle count based on intensity
    const getParticleCount = () => {
      switch (intensity) {
        case 'low':
          return 30;
        case 'high':
          return 100;
        default:
          return 60;
      }
    };
    // Cosmic background - stars and nebulae
    const createCosmicBackground = () => {
      const particleCount = getParticleCount();
      const particles: {
        x: number;
        y: number;
        size: number;
        color: string;
        alpha: number;
        speed: number;
      }[] = [];
      // Create stars
      for (let i = 0; i < particleCount; i++) {
        const hue = Math.random() > 0.5 ? Math.floor(Math.random() * 60) + 180 :
        // blue/purple
        Math.floor(Math.random() * 60) + 330; // pink/red
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 0.5,
          color: `hsl(${hue}, 100%, 70%)`,
          alpha: Math.random() * 0.5 + 0.3,
          speed: Math.random() * 0.2 + 0.1
        });
      }
      // Create nebulae
      const nebulae: {
        x: number;
        y: number;
        radius: number;
        color: string;
        alpha: number;
      }[] = [];
      for (let i = 0; i < 3; i++) {
        const hue = Math.random() > 0.5 ? Math.floor(Math.random() * 60) + 180 :
        // blue/purple
        Math.floor(Math.random() * 60) + 330; // pink/red
        nebulae.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 300 + 100,
          color: `hsl(${hue}, 100%, 70%)`,
          alpha: Math.random() * 0.05 + 0.05
        });
      }
      // Animation loop
      const animate = () => {
        requestAnimationFrame(animate);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Draw nebulae
        nebulae.forEach(nebula => {
          const gradient = ctx.createRadialGradient(nebula.x, nebula.y, 0, nebula.x, nebula.y, nebula.radius);
          gradient.addColorStop(0, `${nebula.color}`);
          gradient.addColorStop(1, 'transparent');
          ctx.globalAlpha = nebula.alpha;
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(nebula.x, nebula.y, nebula.radius, 0, Math.PI * 2);
          ctx.fill();
        });
        // Draw stars
        ctx.globalAlpha = 1;
        particles.forEach(particle => {
          ctx.globalAlpha = particle.alpha;
          ctx.fillStyle = particle.color;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
          // Move stars
          particle.y += particle.speed;
          if (particle.y > canvas.height) {
            particle.y = 0;
            particle.x = Math.random() * canvas.width;
          }
        });
      };
      animate();
    };
    // Garden background - flowing patterns
    const createGardenBackground = () => {
      const particleCount = getParticleCount();
      const particles: {
        x: number;
        y: number;
        size: number;
        color: string;
        alpha: number;
        angle: number;
        speed: number;
        curve: number;
      }[] = [];
      // Create garden particles
      for (let i = 0; i < particleCount; i++) {
        const hue = Math.floor(Math.random() * 60) + 100; // green/teal range
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 4 + 1,
          color: `hsl(${hue}, 70%, 60%)`,
          alpha: Math.random() * 0.4 + 0.2,
          angle: Math.random() * Math.PI * 2,
          speed: Math.random() * 0.5 + 0.2,
          curve: Math.random() * 0.1 - 0.05
        });
      }
      // Animation loop
      const animate = () => {
        requestAnimationFrame(animate);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Draw particles
        particles.forEach(particle => {
          ctx.globalAlpha = particle.alpha;
          ctx.fillStyle = particle.color;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
          // Move particles in a flowing pattern
          particle.x += Math.cos(particle.angle) * particle.speed;
          particle.y += Math.sin(particle.angle) * particle.speed;
          particle.angle += particle.curve;
          // Wrap around edges
          if (particle.x < -10) particle.x = canvas.width + 10;
          if (particle.x > canvas.width + 10) particle.x = -10;
          if (particle.y < -10) particle.y = canvas.height + 10;
          if (particle.y > canvas.height + 10) particle.y = -10;
        });
      };
      animate();
    };
    // Transition background - blend of cosmic and garden
    const createTransitionBackground = () => {
      const particleCount = getParticleCount();
      const particles: {
        x: number;
        y: number;
        size: number;
        color: string;
        alpha: number;
        speed: number;
        type: 'cosmic' | 'garden';
      }[] = [];
      // Create mixed particles
      for (let i = 0; i < particleCount; i++) {
        const isGarden = Math.random() > 0.6;
        let hue;
        if (isGarden) {
          hue = Math.floor(Math.random() * 60) + 100; // green/teal
        } else {
          hue = Math.random() > 0.5 ? Math.floor(Math.random() * 60) + 180 :
          // blue/purple
          Math.floor(Math.random() * 60) + 330; // pink/red
        }
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 3 + 0.5,
          color: `hsl(${hue}, ${isGarden ? '70%' : '100%'}, ${isGarden ? '60%' : '70%'})`,
          alpha: Math.random() * 0.5 + 0.2,
          speed: Math.random() * 0.3 + 0.1,
          type: isGarden ? 'garden' : 'cosmic'
        });
      }
      // Animation loop
      const animate = () => {
        requestAnimationFrame(animate);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Draw particles
        particles.forEach(particle => {
          ctx.globalAlpha = particle.alpha;
          ctx.fillStyle = particle.color;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
          // Move particles
          if (particle.type === 'cosmic') {
            particle.y += particle.speed;
            if (particle.y > canvas.height) {
              particle.y = 0;
              particle.x = Math.random() * canvas.width;
            }
          } else {
            particle.x += Math.cos(particle.y * 0.01) * particle.speed;
            particle.y += particle.speed * 0.5;
            if (particle.y > canvas.height) {
              particle.y = 0;
              particle.x = Math.random() * canvas.width;
            }
            if (particle.x < 0) particle.x = canvas.width;
            if (particle.x > canvas.width) particle.x = 0;
          }
        });
      };
      animate();
    };
    // Initialize based on type
    switch (type) {
      case 'cosmic':
        createCosmicBackground();
        break;
      case 'garden':
        createGardenBackground();
        break;
      case 'transition':
        createTransitionBackground();
        break;
    }
    return () => {
      window.removeEventListener('resize', setCanvasDimensions);
    };
  }, [type, intensity]);
  return <canvas ref={canvasRef} className="fixed inset-0 z-[-1]" style={{
    pointerEvents: 'none'
  }} data-id="element-807" />;
};
export default AnimatedBackground;